import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { MAINTENANCE_EVENT } from "@/lib/api";
import { PUBLIC_CONFIG_KEY, usePublicConfig } from "@/hooks/queries/usePublicConfig";

const DISMISS_KEY = "maintenance_banner_dismissed";
const DEFAULT_MESSAGE = "GenAICore Nexus is undergoing maintenance. Please try again in a few minutes.";

/**
 * App-wide notice while MAINTENANCE_MODE is on. Non-admin users only (admins
 * keep full access and see the state on the settings page). Dismissal lasts
 * for the browser session and is forgotten once maintenance ends.
 */
export function MaintenanceBanner() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const qc = useQueryClient();
  const { data } = usePublicConfig({ refetchInterval: isAdmin ? undefined : 60_000 });
  const [message, setMessage] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem(DISMISS_KEY) === "1");

  // A request refused with 503 MAINTENANCE: show the server's message right away.
  useEffect(() => {
    const onMaintenance = (e: Event) => {
      const msg = (e as CustomEvent<{ message?: string }>).detail?.message;
      setMessage(msg || DEFAULT_MESSAGE);
      qc.invalidateQueries({ queryKey: PUBLIC_CONFIG_KEY });
    };
    window.addEventListener(MAINTENANCE_EVENT, onMaintenance);
    return () => window.removeEventListener(MAINTENANCE_EVENT, onMaintenance);
  }, [qc]);

  // Maintenance over: forget the dismissal and any cached message.
  useEffect(() => {
    if (data && !data.maintenance) {
      sessionStorage.removeItem(DISMISS_KEY);
      setDismissed(false);
      setMessage(null);
    }
  }, [data]);

  const active = data ? data.maintenance : message !== null;
  if (isAdmin || !active || dismissed) return null;

  return (
    <div
      role="status"
      className="mb-3 mt-1 flex items-start gap-3 rounded-card bg-card p-4 ring-1 ring-inset ring-warning/40"
    >
      <span className="h-9 w-9 shrink-0 rounded-xl bg-warning/10 text-warning flex items-center justify-center" aria-hidden>
        <AlertTriangle className="h-[18px] w-[18px]" strokeWidth={1.8} />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-[15px] font-medium">Scheduled maintenance</p>
        <p className="text-[13px] text-muted-foreground">
          {message || DEFAULT_MESSAGE} Requests may fail until it finishes.
        </p>
      </div>
      <Button
        variant="ghost"
        size="icon"
        className="h-9 w-9 shrink-0"
        aria-label="Dismiss maintenance notice"
        onClick={() => {
          sessionStorage.setItem(DISMISS_KEY, "1");
          setDismissed(true);
        }}
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
}
